import * as React from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  CorePinterestBoardFieldsFragment,
  PinterestSearchContentType,
  PinterestSearchSortType,
  PinterestSearchType,
  ThirdPartyPost,
  useSearchPinterestFollowedBoardsLazyQuery,
  useSearchPinterestPostsLazyQuery
} from '../../../../models/graphql';
import {
  ContentFilterPinterestController,
  ContentFilterPinterestControllerProps
} from './ContentFilterPinterest.controller';
import { gql, useApolloClient } from '@apollo/client';
import { FindContentContext, SearchStepEnum } from '../FindContentProvider';
import { ScrollContext } from '../../../../providers/ScrollProvider';

export type SearchPinterestPostVariables = {
  socialProfileId: string;
  searchType: PinterestSearchType;
  contentType: PinterestSearchContentType;
  sortType: PinterestSearchSortType;
  externalId: string;
  boardId?: string;
  term?: string;
};

export type ContentFilterPinterestApolloProps = {
  formInstance: UseFormReturn;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
};

gql`
  query SearchPinterestFollowedBoards($socialProfileId: ID!) {
    searchPinterestFollowedBoards(socialProfileId: $socialProfileId) {
      ...CorePinterestBoardFields
    }
  }

  query SearchPinterestPosts(
    $socialProfileId: ID!
    $searchType: PinterestSearchType!
    $contentType: PinterestSearchContentType!
    $sortType: PinterestSearchSortType!
    $externalId: String!
    $term: String
    $cursor: String
  ) {
    searchPinterestPosts(
      socialProfileId: $socialProfileId
      searchType: $searchType
      contentType: $contentType
      sortType: $sortType
      externalId: $externalId
      term: $term
      cursor: $cursor
    ) {
      items {
        ...ThirdPartyPostFields
      }
      cursor
    }
  }
`;

export const ContentFilterPinterestApollo = (props: ContentFilterPinterestApolloProps): JSX.Element => {
  const { cache } = useApolloClient();
  const [searchParameters, setSearchParameters] = useState<SearchPinterestPostVariables | undefined>(undefined);
  const [pinterestBoards, setPinterestBoards] = useState<CorePinterestBoardFieldsFragment[]>([]);
  const [searchPinterestFollowedBoards, pinterestBoardsResults] = useSearchPinterestFollowedBoardsLazyQuery();
  const [searchPinterestPosts, pinterestPostsResults] = useSearchPinterestPostsLazyQuery();
  const [reachedLimit, setReachedLimit] = useState<boolean>(false);

  const { data: posts, fetchMore } = pinterestPostsResults;
  const { data: boards } = pinterestBoardsResults;
  const { setLoading, setLoadingMore, setPosts, setSelectedPosts, setSearchStep, setHasSearched } =
    useContext(FindContentContext);
  const { setOnScrollBottom } = useContext(ScrollContext);

  const fetchPinterestBoards = useCallback(
    async (profileId: string) => {
      await searchPinterestFollowedBoards({ variables: { socialProfileId: profileId } }).then(results => {
        if (results.error) {
          throw results.error;
        }
      });
    },
    [searchPinterestFollowedBoards]
  );

  const fetchPinterestPosts = useCallback(
    async (parameters: SearchPinterestPostVariables) => {
      setPosts([]);
      setLoading(true);

      await searchPinterestPosts({ variables: parameters })
        .then(results => {
          if (results.error) {
            throw results.error;
          } else if (results.data?.searchPinterestPosts.items.length) {
            setSearchStep(SearchStepEnum.TWO);
          }

          setHasSearched(true);
        })
        .finally(() => setLoading(false));
    },
    [searchPinterestPosts, setHasSearched, setLoading, setPosts, setSearchStep]
  );

  const purgePinterestCache = useCallback(() => {
    cache.evict({ fieldName: 'searchPinterestPosts' });
    cache.gc();

    setPinterestBoards([]);
    setPosts([]);
    setSelectedPosts([]);
    setReachedLimit(false);
  }, [cache, setPosts, setSelectedPosts]);

  useEffect(() => {
    if (posts?.searchPinterestPosts.items) {
      setPosts(posts.searchPinterestPosts.items as ThirdPartyPost[]);
    }
  }, [posts, setPosts]);

  useEffect(() => {
    if (boards?.searchPinterestFollowedBoards) {
      setPinterestBoards(boards.searchPinterestFollowedBoards);
    }
  }, [boards]);

  useEffect(() => {
    setOnScrollBottom(() => {
      // Don't proceed if we already reached limit
      if (!reachedLimit && !!searchParameters?.socialProfileId) {
        setLoadingMore(true);

        const cursor = posts?.searchPinterestPosts.cursor;
        fetchMore({ variables: { ...searchParameters, cursor } })
          .then(results => {
            const newItems = results.data.searchPinterestPosts.items;
            // If no results, we should not fetch more
            setReachedLimit(newItems.length === 0);
            setPosts(prev => [...prev, ...newItems]);
          })
          .finally(() => {
            setLoadingMore(false);
          });
      }
    });
  }, [fetchMore, posts, reachedLimit, searchParameters, setLoadingMore, setOnScrollBottom, setPosts]);

  const controllerProps: ContentFilterPinterestControllerProps = {
    ...props,
    searchParameters,
    setSearchParameters,
    pinterestBoards,
    fetchPinterestPosts,
    fetchPinterestBoards,
    purgePinterestCache
  };

  return <ContentFilterPinterestController {...controllerProps} />;
};
